import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TutorialService from "@/Service/TutorialService";
import { useContributorsState } from "@/store";
import SearchTextField from "@/components/Search/SearchTextField";
import MuiClipDateRangePicker from "@/components/Clips/MuiClipDateRangePicker";
import Pagination from "@/components/Util/Pagination";
import DataLoader from "@/components/Loader/DataLoader/DataLoader";
import NoDataState from "@/components/EmptyData/NoDataState";
import { ClipFormContainer } from "@/components/Util/Form.elements";
import { SearchFormWrapper, SearchActionsRow } from "@/components/PackagesSection/SearchForm.elements";
import { TUTORIALS_NEWEST_FIRST, getTutorial } from "./registry";
import { PROGRESS_CHIP } from "./TutorialCard";

// The API serialises LocalDateTime as an array or ISO string depending on the
// Jackson config; accept both so the table never shows "Invalid Date".
export const toDate = (value) => {
  if (!value) return null;
  const date = Array.isArray(value)
    ? new Date(value[0], (value[1] || 1) - 1, value[2] || 1, value[3] || 0, value[4] || 0, value[5] || 0)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatWhen = (date) =>
  date ? date.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const rowDate = (row) => toDate(row.status === "COMPLETED" ? row.completedAt || row.updatedAt : row.updatedAt);

const EMPTY_FILTERS = { searchTerm: "", tutorialId: "", status: "", startDate: undefined, endDate: undefined };

/**
 * Pure filter so the table logic is unit-testable without the DOM: the header
 * "All Contributors" selector (uid), free-text name/email, tutorial, result,
 * and an inclusive date range (end date runs to the end of that day).
 */
export const filterTutorialResults = (rows, { contributorId, searchTerm, tutorialId, status, startDate, endDate }) => {
  const term = (searchTerm || "").trim().toLowerCase();
  const from = startDate ? new Date(startDate) : null;
  const to = endDate ? new Date(endDate) : null;
  if (from) from.setHours(0, 0, 0, 0);
  if (to) to.setHours(23, 59, 59, 999);
  return (rows || []).filter((row) => {
    if (contributorId && row.userUid !== contributorId) return false;
    if (tutorialId && row.tutorialId !== tutorialId) return false;
    if (status && row.status !== status) return false;
    if (term) {
      const haystack = `${row.name || ""} ${row.email || ""} ${row.userUid || ""}`.toLowerCase();
      if (!haystack.includes(term)) return false;
    }
    if (from || to) {
      const when = rowDate(row);
      if (!when) return false;
      if (from && when < from) return false;
      if (to && when > to) return false;
    }
    return true;
  });
};

const HeadCell = ({ children, sx, ...rest }) => (
  <TableCell
    {...rest}
    sx={{ color: "#f7fafaa6", fontSize: "10px", fontWeight: 800, letterSpacing: "0.045em", textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", whiteSpace: "nowrap", py: 1.4, px: 1.75, ...sx }}
  >
    {children}
  </TableCell>
);

const BodyCell = ({ children, sx, ...rest }) => (
  <TableCell
    {...rest}
    sx={{ color: "#f7fafa", fontSize: "13px", borderBottom: "1px solid rgba(255,255,255,0.06)", verticalAlign: "middle", py: 1.3, px: 1.75, ...sx }}
  >
    {children}
  </TableCell>
);

const selectSx = { minWidth: 0 };

/**
 * Admin → "Results" tab: who passed which tutorial. Same shape as the Uploads /
 * Users lists — filter bar, table, pagination — and it honours the header
 * "All Contributors" selector like the other lists do. Data is small (one row
 * per person × tutorial), so filtering and paging happen client-side.
 */
export const TutorialResultsTab = () => {
  const { selectedContributorId } = useContributorsState();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    let cancelled = false;
    TutorialService.getProgress()
      .then((data) => { if (!cancelled) setRows(Array.isArray(data) ? data : []); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Could not load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(
    () => filterTutorialResults(rows, { ...filters, contributorId: selectedContributorId }),
    [rows, filters, selectedContributorId]
  );
  const totalPages = Math.ceil(filtered.length / pageSize);
  const safePage = Math.min(currentPage, Math.max(0, totalPages - 1));
  const pageRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const applyFilters = () => { setFilters(draft); setCurrentPage(0); };
  const clearFilters = () => { setDraft(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setCurrentPage(0); };

  return (
    <Box data-testid="tutorials-results" sx={{ pt: 1, pb: 4 }}>
      <ClipFormContainer>
        <SearchFormWrapper>
          <Box sx={{ minWidth: 0 }}>
            <SearchTextField
              value={draft.searchTerm}
              onChange={(e) => setDraft((d) => ({ ...d, searchTerm: e.target.value }))}
              label="Search user"
              placeholder="Name or email"
              inputProps={{ "data-testid": "tutorials-results-search" }}
            />
          </Box>
          <FormControl sx={selectSx}>
            <InputLabel id="tutorials-results-tutorial-label">Tutorial</InputLabel>
            <Select
              labelId="tutorials-results-tutorial-label"
              label="Tutorial"
              value={draft.tutorialId}
              onChange={(e) => setDraft((d) => ({ ...d, tutorialId: e.target.value }))}
              data-testid="tutorials-results-tutorial"
            >
              <MenuItem value="">All tutorials</MenuItem>
              {TUTORIALS_NEWEST_FIRST.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={selectSx}>
            <InputLabel id="tutorials-results-status-label">Result</InputLabel>
            <Select
              labelId="tutorials-results-status-label"
              label="Result"
              value={draft.status}
              onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
              data-testid="tutorials-results-status"
            >
              <MenuItem value="">All results</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="SKIPPED">Skipped</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ minWidth: 0 }}>
            <MuiClipDateRangePicker
              label="Start Date"
              value={draft.startDate}
              onChange={(startDate) => setDraft((d) => ({ ...d, startDate }))}
              format="yyyy/MM/dd"
              slotProps={{ textField: { placeholder: "YYYY/MM/DD", fullWidth: true } }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <MuiClipDateRangePicker
              label="End Date"
              value={draft.endDate}
              onChange={(endDate) => setDraft((d) => ({ ...d, endDate }))}
              format="yyyy/MM/dd"
              slotProps={{ textField: { placeholder: "YYYY/MM/DD", fullWidth: true } }}
            />
          </Box>
          <SearchActionsRow>
            <Button
              onClick={applyFilters}
              variant="contained"
              data-testid="tutorials-results-apply"
              sx={{ color: "#fff", fontWeight: "bold", borderRadius: "8px", padding: "18px 38px", textTransform: "none", minWidth: "160px", "&:hover": { backgroundColor: "#8095B7" } }}
            >
              Search
            </Button>
            <Button
              onClick={clearFilters}
              variant="outlined"
              data-testid="tutorials-results-clear"
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", fontWeight: "bold", borderRadius: "8px", padding: "18px 30px", textTransform: "none", minWidth: "140px" }}
            >
              Clear Filters
            </Button>
          </SearchActionsRow>
        </SearchFormWrapper>
      </ClipFormContainer>

      {!loading && !error && (
        <Typography sx={{ px: 1, pb: 1, fontSize: 12, color: "rgba(247,250,250,0.6)" }} data-testid="tutorials-results-summary">
          {filtered.length} result{filtered.length === 1 ? "" : "s"} · {new Set(filtered.map((r) => r.userUid)).size} people ·{" "}
          {filtered.filter((r) => r.status === "COMPLETED").length} completed
        </Typography>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}><DataLoader /></div>
      ) : error ? (
        <Typography sx={{ p: 2, fontSize: 13, color: "#FF9AA4" }} data-testid="tutorials-results-error">{error}</Typography>
      ) : filtered.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}><NoDataState /></div>
      ) : (
        <>
          <Box sx={{ border: "1px solid rgba(255,255,255,0.09)", borderRadius: "16px", overflow: "hidden", background: "rgba(20,27,40,0.74)" }}>
            <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 640 }} data-testid="tutorials-results-table">
                <TableHead>
                  <TableRow>
                    <HeadCell>User</HeadCell>
                    <HeadCell>Tutorial</HeadCell>
                    <HeadCell>Result</HeadCell>
                    <HeadCell align="right">Date</HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((r) => {
                    const chip = PROGRESS_CHIP[r.status] || PROGRESS_CHIP.SKIPPED;
                    const tutorial = getTutorial(r.tutorialId);
                    return (
                      <TableRow key={`${r.userUid}:${r.tutorialId}`} hover data-testid="tutorials-results-row" sx={{ "&:hover": { background: "rgba(255,255,255,0.035)" } }}>
                        <BodyCell>
                          <Box sx={{ fontWeight: 700 }}>{r.name || r.email || r.userUid}</Box>
                          {r.name && r.email && (
                            <Box sx={{ fontSize: 12, color: "rgba(247,250,250,0.55)" }}>{r.email}</Box>
                          )}
                        </BodyCell>
                        <BodyCell>{tutorial?.title || r.tutorialId}</BodyCell>
                        <BodyCell>
                          <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 999, fontSize: 12, fontWeight: 700, background: chip.bg, color: chip.fg, whiteSpace: "nowrap" }}>
                            {chip.label}
                          </Box>
                          {tutorial && r.versionSeen != null && r.versionSeen < tutorial.version && (
                            <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: "rgba(247,250,250,0.5)" }}>(older version)</Box>
                          )}
                        </BodyCell>
                        <BodyCell align="right" sx={{ whiteSpace: "nowrap" }}>{formatWhen(rowDate(r))}</BodyCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(0); }}
          />
        </>
      )}
    </Box>
  );
};
